/**
 * The English dictionary, and the shape of every other one. Flat keys;
 * `ar.ts` is typed against this, so a missing translation is a compile error
 * rather than a key rendered raw in production (§11.2).
 *
 * `{name}` placeholders are filled by `t(key, { name })`.
 */
export const en = {
  // --- chrome ---------------------------------------------------------
  'app.name': 'explorer',
  'app.tagline': 'parsed filings, next to the paper they came from',
  'nav.explore': 'Filter',
  'nav.results': 'Companies',
  'nav.about': 'Corpus',
  'nav.back': 'Back',
  'action.refresh': 'Refresh',
  'action.retry': 'Try again',
  'action.close': 'Close',
  'action.clear': 'Clear',
  'action.selectAll': 'Select all',
  'action.selectNone': 'None',
  'action.expand': 'Expand',
  'action.collapse': 'Collapse',
  'action.copy': 'Copy',
  'action.copied': 'Copied',
  'action.showMore': 'Show more',
  'action.showLess': 'Show less',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.system': 'System',
  'theme.label': 'Theme',
  'lang.label': 'Language',
  'lang.en': 'English',
  'lang.ar': 'العربية',
  'display.label': 'Display',
  'display.title': 'Display settings',
  'density.label': 'Text and control size',
  'density.auto': 'Automatic',
  'density.autoHint': 'Larger on a touchscreen, dense with a mouse.',
  'density.comfortable': 'Comfortable',
  'density.compact': 'Compact',

  // --- generic --------------------------------------------------------
  'common.loading': 'Loading…',
  'common.error': 'Something failed',
  'common.errorWithId': 'Request {id} failed.',
  'common.notRecorded': 'not recorded',
  'common.none': 'none',
  'common.of': 'of',
  'common.and': 'and',
  'common.documents': '{n} documents',
  'common.documents_one': '{n} document',
  'common.companies': '{n} companies',
  'common.companies_one': '{n} company',
  'common.pages': '{n} pages',
  'common.pages_one': '{n} page',
  'common.facts': '{n} facts',
  'common.facts_one': '{n} fact',
  'common.checks': '{n} checks',
  'common.checks_one': '{n} check',
  'common.rows': '{n} rows',
  'common.rows_one': '{n} row',

  // --- filter screen --------------------------------------------------
  'filter.title': 'Parsed corpus',
  'filter.corpusLine': '{documents} · {companies} · {from}–{to}',
  'filter.sector': 'Sector',
  'filter.industry': 'Industry',
  'filter.companies': 'Companies',
  'filter.period': 'Period',
  'filter.docType': 'Document type',
  'filter.locale': 'Language',
  'filter.year': 'Year',
  'filter.quality': 'Quality',
  'filter.quality.any': 'Any',
  'filter.quality.high': '≥ 90% pass',
  'filter.quality.medium': '75–90%',
  'filter.quality.low': 'below 75%',
  'filter.quality.note':
    'Every document in this corpus has at least one failing check, so a "clean only" filter would return nothing. The bands grade what is there.',
  'filter.selected': '{n} of {total} selected',
  'filter.allIncluded': 'all {total} included',
  'filter.onlyOption': 'the only option',
  'filter.explore': 'Explore',
  'filter.exploreAll': 'Explore all {documents}',
  'filter.exploreCount': '{documents} across {companies}',
  'filter.empty': 'Nothing matches this selection',
  'filter.emptyHint': 'Widen a filter — {hint}',
  'filter.pruned': '{n} removed — no longer in {level}',
  'filter.pruned_one': '{n} removed — no longer in {level}',
  'filter.passRate': 'pass rate',
  'filter.docsShort': '{n} docs',
  'filter.reset': 'Reset filters',

  // --- results --------------------------------------------------------
  'results.title': 'Companies',
  'results.count': '{companies} · {documents}',
  'results.editFilter': 'Edit filter',
  'results.sort': 'Sort',
  'results.sort.symbol': 'Symbol',
  'results.sort.name': 'Name',
  'results.sort.documents': 'Documents',
  'results.sort.quality': 'Pass rate',
  'results.coverage': 'Coverage',
  'results.coverageLegend': 'filled = parsed',
  'results.openReview': '{n} open',
  'results.passShort': '{pct} checks pass',
  'results.empty': 'No company matches this filter',
  'results.emptyHint': 'Go back and widen it.',

  // --- company --------------------------------------------------------
  'company.parsedDocuments': '{n} parsed documents',
  'company.parsedDocuments_one': '{n} parsed document',
  'company.tab.overview': 'Overview',
  'company.tab.financials': 'Financials',
  'company.tab.notes': 'Notes',
  'notes.numberFrom': 'Note number from the page classification. Where it disagrees with the printed ordinal beside it, the disagreement is the data, not a display bug.',
  'company.tab.auditor': 'Auditor',
  'company.tab.documents': 'Documents',
  'company.tab.quality': 'Quality',
  'company.tab.governance': 'Governance',
  'company.tab.remuneration': 'Remuneration',
  'company.tab.subsidiaries': 'Subsidiaries',

  // --- announcements, and the cross-check against them ----------------
  'company.tab.announcements': 'Announcements',
  'company.tab.crosscheck': 'Cross-check',

  'ann.none': 'No announcements captured',
  'ann.noneDetail': 'Run capture/company_announcements.py for this company.',
  'ann.total': 'Announcements',
  'ann.financial': 'Results filings',
  'ann.financialHint': 'the ones carrying figures',
  'ann.figures': 'Figures',
  'ann.span': 'Filed between',
  'ann.units': 'Unit multipliers',
  'ann.unitsVary': 'more than one — never assume a scale',
  'ann.unit': 'unit',
  'ann.era': 'template',
  'ann.scope.financial': 'Results only',
  'ann.scope.all': 'All',
  'ann.filter': 'filter by title, quarter or id…',
  'ann.truncated': 'Showing the most recent announcements only.',
  'ann.correction': 'Correction',
  'ann.kind.annual': 'Annual',
  'ann.kind.interim': 'Interim',
  'ann.openEn': 'Announcement (EN)',
  'ann.openAr': 'Announcement (AR)',
  'ann.noFigures': 'This announcement carries no figure grid.',
  'ann.item': 'Line item',
  'ann.value': 'Value',
  'ann.priorYear': 'Prior year',
  'ann.change': 'Change',
  'ann.priorQuarter': 'Prior quarter',
  'ann.changeQ': 'Change vs prior Q',
  'ann.gridHint.quarter': 'the discrete three months',
  'ann.gridHint.period': 'cumulative year to date',
  'ann.gridHint.annual': 'the full year',

  'crosscheck.none': 'No cross-check has been run',
  'crosscheck.noneDetail': 'Run parser.validate_announcements for this company.',
  'crosscheck.accuracy': "Extraction accuracy",
  'crosscheck.accuracyHint': "excludes differences that are not ours",
  'crosscheck.ourFaults': "Our errors",
  'crosscheck.ourFaultsHint': "the extraction misread the page",
  'crosscheck.exactWithin': "{exact} exact · {within} within precision",
  'crosscheck.agreementHint': "raw — both sources say the same thing",
  'crosscheck.notDefects': "Not defects",
  'crosscheck.notDefectsHint': "attributable vs total, restatements",
  'crosscheck.issuerSide': "Issuer's filing",
  'crosscheck.issuerSideHint': "the announcement is the wrong side",
  'crosscheck.comparisons': 'Comparisons',
  'crosscheck.agreement': 'Agreement',
  'crosscheck.exact': 'Exact',
  'crosscheck.exactHint': 'to the riyal',
  'crosscheck.within': 'Within precision',
  'crosscheck.withinHint': "inside the form's own rounding",
  'crosscheck.mismatches': 'Mismatches',
  'crosscheck.errWarn': '{errors} error / {warnings} warning',
  'crosscheck.documents': 'Documents',
  'crosscheck.allAgree': 'Every comparison agreed within the announcement\u2019s own precision.',
  'crosscheck.byCause': 'Findings by cause',
  'crosscheck.byCauseHint': 'Grouped by what the disagreement actually is — the work differs per group.',
  'crosscheck.group.parser': 'Ours',
  'crosscheck.group.issuer': 'Issuer',
  'crosscheck.group.benign': 'Not a defect',
  'crosscheck.error': 'Error',
  'crosscheck.warning': 'Warning',
  'crosscheck.extracted': 'Extracted',
  'crosscheck.announced': 'Announced',
  'crosscheck.delta': 'Delta',
  'crosscheck.tolerance': 'Tolerance',
  'crosscheck.shape': 'Shape',
  'crosscheck.matchedBy': 'Matched by',
  'crosscheck.matched.label': 'row label',
  'crosscheck.matched.concept': 'concept',
  'crosscheck.printedScale': 'scale applied {printed}',
  'crosscheck.printedUnit': 'printed {printed} × unit {unit}',
  'crosscheck.settled': 'Settled by arithmetic — no judge call made',
  'crosscheck.ownQuarters': 'its own quarters imply',
  'crosscheck.judge': 'AI judge',
  'crosscheck.confidence': 'confidence',
  'crosscheck.whereToLook': 'Where to look',
  'crosscheck.evidence': 'Full evidence',
  'crosscheck.agreements': 'All agreements',
  'crosscheck.agreementsHint': 'Every comparison that passed, cited the same way — the evidence that the check is still matching.',
  'crosscheck.period': 'Period',
  'crosscheck.lineItem': 'Line item',
  'crosscheck.grid': 'Grid',
  'crosscheck.source': 'Source page',
  'crosscheck.announcement': 'Announcement',
  'crosscheck.openPage': 'Open page {page} in the viewer',
  'crosscheck.anId': 'anId',
  'crosscheck.factId': 'fact id',
  'crosscheck.documentId': 'document id',
  'crosscheck.page': 'page',
  'crosscheck.form': 'form',
  'crosscheck.published': 'published',
  'crosscheck.locale': 'locale',
  'crosscheck.columnRole': 'column role',
  'crosscheck.rowLabel': 'row label',
  'crosscheck.columnLabel': 'column header',
  'crosscheck.periodType': 'period type',
  'crosscheck.yes': 'yes',
  'crosscheck.priorYearMatch': 'equals prior-year figure',
  'crosscheck.gridName.quarter': 'Quarter',
  'crosscheck.gridName.period': 'Cumulative',
  'crosscheck.gridName.annual': 'Annual',
  'crosscheck.gridName.accumulated': 'Accumulated',
  'crosscheck.concept.revenue': 'Revenue',
  'crosscheck.concept.gross_profit': 'Gross profit',
  'crosscheck.concept.operating_profit': 'Operating profit',
  'crosscheck.concept.net_profit': 'Net profit',
  'crosscheck.concept.total_comprehensive_income': 'Total comprehensive income',
  'crosscheck.concept.total_shareholders_equity': "Total shareholders' equity",
  'crosscheck.concept.eps': 'Earnings per share',
  'crosscheck.verdict.extraction_error': 'Extraction error',
  'crosscheck.verdict.announcement_error': 'Announcement error',
  'crosscheck.verdict.legitimate_difference': 'Legitimate difference',
  'crosscheck.verdict.concept_mismatch': 'Concept mismatch',
  'crosscheck.verdict.unclear': 'Unclear',
  'crosscheck.cause.parse_digit': 'Misread digit',
  'crosscheck.cause.parse_scale': 'Wrong scale applied',
  'crosscheck.cause.parse_sign': 'Lost or added sign',
  'crosscheck.cause.parse_column': 'Wrong column read',
  'crosscheck.cause.parse_other': 'Unexplained gap',
  'crosscheck.cause.announcement_arithmetic': 'Announcement contradicts itself',
  'crosscheck.cause.announcement_unit': "Issuer's unit slip",
  'crosscheck.cause.announcement_value': 'Announcement differs',
  'crosscheck.cause.scope_attributable': 'Attributable vs total',
  'crosscheck.cause.scope_restated': 'Restated comparative',
  'crosscheck.cause.concept_mismatch': 'Not the same line item',
  'crosscheck.cause.unresolved': 'Undetermined',
  'crosscheck.causeHint.parse_digit': 'The extraction read one numeral wrong — typically an Arabic-Indic confusion (٧/٨, ٠/٥, ١/٢). The fix belongs in the extraction tier.',
  'crosscheck.causeHint.parse_scale': "The extracted value is a clean power of ten away from the announced one: the document's rounding factor was applied wrongly, or not at all.",
  'crosscheck.causeHint.parse_sign': 'Same magnitude, opposite sign — a parenthesised negative read as positive, or the reverse.',
  'crosscheck.causeHint.parse_column': "The extracted value equals the announcement's prior-year figure, so the extraction read one column across.",
  'crosscheck.causeHint.parse_other': 'The gap has no recognised shape. Open the page and read the evidence.',
  'crosscheck.causeHint.announcement_arithmetic': 'The announcement\u2019s own quarters do not sum to its cumulative figure, and the extraction matches the quarters. The typo is in the filing form, not in the reading \u2014 proved by arithmetic, not judged.',
  'crosscheck.causeHint.announcement_unit': 'The digits match but the magnitude does not: the issuer typed the figure in one unit into a form declaring another. The extraction is right; the announcement is not.',
  'crosscheck.causeHint.announcement_value': 'The announced figure is a different number from the audited statement — usually a preliminary filing the final statements later restated.',
  'crosscheck.causeHint.scope_attributable': "Not a defect: the announcement reports the parent shareholders' share while the statement line is the total including non-controlling interests.",
  'crosscheck.causeHint.scope_restated': "Not a defect: the figure came from a later filing's comparative column, which the issuer had restated.",
  'crosscheck.causeHint.concept_mismatch': 'The two rows are not comparable; the crosswalk paired them wrongly and should be tightened.',
  'crosscheck.causeHint.unresolved': 'Neither the delta shape nor the judge could decide it.',
  'company.summary': 'Business',
  'company.summary.other': 'Show the {lang} summary',
  'company.ledger': 'Extracted',
  'company.ledger.facts': 'facts',
  'company.ledger.tables': 'tables',
  'company.ledger.notes': 'notes',
  'company.ledger.checks': 'checks',
  'company.latestAuditor': 'Latest auditor',
  'company.opinion': 'Opinion',
  'company.signer': 'Signed by',
  'company.licence': 'Licence',
  'company.firm': 'Firm',
  'company.reportDate': 'Report date',
  'company.currency': 'Currency',
  'company.rounding': 'Rounding',
  'company.lastParsed': 'Last parsed',

  // --- financials -----------------------------------------------------
  'fin.statement': 'Statement',
  'fin.period': 'Period',
  'fin.noConcepts':
    'Rows are aligned by printed label; this database has no concept mapping, so wording changes between filings will not line up.',
  'fin.noConceptsFix': 'Run `python -m parser.concepts --seed` to fix that.',
  'fin.source': 'Source',
  'fin.goToPage': 'Page {n}',

  // --- documents list -------------------------------------------------
  'docs.type': 'Type',
  'docs.period': 'Period',
  'docs.periodEnd': 'Period end',
  'docs.facts': 'Facts',
  'docs.year': 'Year',
  'docs.lang': 'Language',
  'docs.pages': 'Pages',
  'docs.status': 'Status',
  'docs.passRate': 'Pass',
  'docs.review': 'Open',
  'docs.xbrl': 'XBRL',

  // --- viewer ---------------------------------------------------------
  'viewer.page': 'Page {n}',
  'viewer.pageOf': 'page {n} of {total}',
  'viewer.paneImage': 'Page',
  'viewer.paneExtraction': 'Extracted',
  'viewer.paneLabel': 'Which pane to show',
  'viewer.details': 'Filing details',
  'viewer.more': 'More',
  'viewer.failingPages_one': '1 failing page',
  'viewer.failingPages': '{n} failing pages',
  'viewer.goToPage': 'Go to page',
  'viewer.firstPage': 'First page',
  'viewer.lastPage': 'Last page',
  'viewer.rail': 'Pages',
  'viewer.tier': 'Tier',
  'viewer.fitWidth': 'Fit width',
  'viewer.fitPage': 'Fit page',
  'viewer.actualSize': 'Actual size',
  'viewer.zoomIn': 'Zoom in',
  'viewer.zoomOut': 'Zoom out',
  'viewer.asSent': 'As sent',
  'viewer.fullPage': 'Full page',
  'viewer.overlap': 'Crop overlap',
  'viewer.pdf': 'PDF',
  'viewer.raw': 'Raw reply',
  'viewer.digits': 'Digits',
  'viewer.digitsPrinted': 'As printed',
  'viewer.digitsWestern': 'Transliterated',
  'viewer.shortcuts': 'Shortcuts',
  'viewer.tables': 'Tables',
  'viewer.signatories': 'Signed by',
  'viewer.signatoriesHint': 'Roles as printed. The signatures themselves are handwritten — they are on the page image beside this.',
  'viewer.source.scan': 'scan',
  'viewer.source.text': 'text layer',
  'viewer.source.dpi': '{n} dpi',
  'viewer.source.chars': '{n} chars',
  'viewer.source.noText': 'no text layer',
  'viewer.source.ink': '{pct} ink',
  'viewer.source.rotated': 'rotated {n}°',
  'viewer.source.reversedDigits': '{n} reversed digit groups',
  'viewer.goToNote': 'Note {n}, page {p}',
  'viewer.narratives': 'Narrative',
  'viewer.factsTyped': 'Typed facts',
  'viewer.factsHint': 'the same content as the table above, per cell, with every conversion shown',
  'viewer.checksFailed': '{n} failed',
  'viewer.checksAllPass': 'all {n} pass',
  'viewer.noChecks': 'no checks on this page',
  'viewer.tableCount': '{n} tables',
  'viewer.tableCount_one': '{n} table',
  'viewer.auditors': 'Auditor',
  'viewer.kam': 'Key audit matters',
  'viewer.nothingExtracted': 'Nothing was extracted from this page',
  'viewer.nothingExtractedWhy':
    'The page is classified {pageType}, and the run produced no rows for it. That is a finding, not a gap in this screen.',
  'viewer.skippedPage': 'This page was deliberately skipped',
  'viewer.skippedPageWhy': 'Pages classified {pageType} are never sent to the model.',
  'viewer.noImage': 'No image for this page',
  'viewer.imagePreparing': 'Preparing the image…',
  'viewer.imageCommand': 'Produce it with:',
  'viewer.nextFailure': 'Next failing page',
  'viewer.prevFailure': 'Previous failing page',
  'viewer.noFailures': 'No failing checks in this document',
  'viewer.syncScroll': 'Reset both panes on page change',
  'viewer.repaired': 'Repaired',
  'viewer.escalations': 'Escalations',
  'viewer.flags': 'Flags',
  'viewer.illegible': 'Illegible regions',

  // --- fact audit -----------------------------------------------------
  'fact.raw': 'printed',
  'fact.western': 'transliterated',
  'fact.printed': 'as a number',
  'fact.value': 'scaled',
  'fact.scale': 'scale',
  'fact.label': 'label',
  'fact.column': 'column',
  'fact.period': 'period',
  'fact.nil': 'nil',
  'fact.parens': 'from parentheses',
  'fact.total': 'total',
  'fact.level': 'level',
  'fact.note': 'note',
  'fact.concept': 'concept',
  'fact.derivedWestern': 'transliteration filled in by the pipeline, not by the model',

  // --- quality --------------------------------------------------------
  'quality.title': 'Quality',
  'quality.passRate': 'Pass rate',
  'quality.passRateOf': '{pct} ({passed} of {total} checks)',
  'quality.errors': '{n} errors',
  'quality.warnings': '{n} warnings',
  'quality.openReview': '{n} open review items',
  'quality.openReview_one': '{n} open review item',
  'quality.xbrl': 'XBRL agreement',
  'quality.xbrlNone': 'no XBRL sibling was published for this filing',
  'quality.expected': 'expected',
  'quality.actual': 'actual',
  'quality.delta': 'difference',
  'quality.detail': 'Detail',
  'quality.byCheck': 'By check',
  'quality.dominant':
    '{pct} of open review items are a single check, {check}. Whether that is an extraction problem or an over-strict check is exactly what this app exists to let you decide.',
  'quality.severity.error': 'error',
  'quality.severity.warn': 'warning',

  // --- empty states ---------------------------------------------------
  'empty.remuneration':
    'No remuneration rows. This data comes from board reports, and this corpus has one — which has not finished the pipeline.',
  'empty.governance':
    'No board or committee rows. This data comes from board reports, and this corpus has one — which has not finished the pipeline.',
  'empty.subsidiaries': 'No subsidiary rows were extracted from these filings.',
  'empty.metrics': 'No operational or ESG metrics were extracted from these filings.',
  'empty.generic': 'Nothing here',

  // --- about ----------------------------------------------------------
  'about.title': 'Corpus',
  'about.whatIsParsed': 'What is parsed',
  'about.whatIsNot': 'What is not',
  'about.complete': 'Complete',
  'about.completeWhy':
    'A document counts as complete when it has been through every phase — status verified or needs review. There are no documents at verified: phase 12 assigns needs review whenever any check failed, and on this corpus something fails in nearly every filing. Keying on verified would produce an empty app.',
  'about.incomplete': 'Not yet complete',
  'about.incompleteWhy': 'These are excluded from every list in this app.',
  'about.lastRun': 'Most recent run',
  'about.emptyTables': 'Tables with no rows',
  'about.orphans': 'Parsed symbols that are not listed companies',
  'about.withFailures': 'Documents with a failing check',
  'about.indexes': 'Page-path indexes',
  'about.concepts': 'Concept vocabulary',

  // --- gaps -----------------------------------------------------------
  'gaps.title': 'Gaps',
  'gaps.subtitle': 'What is missing from this company’s parse, and what would close it',
  'gaps.none': 'Nothing is missing',
  'gaps.noneWhy':
    'Every filing capture holds has been parsed, every document reached a terminal status, and every planned page was read.',
  'gaps.summary.complete': '{n} parsed',
  'gaps.summary.missing': '{n} never read',
  'gaps.summary.stalled': '{n} stalled',
  'gaps.summary.expected': 'of {n} capture holds',
  'gaps.summary.review': '{items} open checks over {pages} pages',
  'gaps.showing': 'showing {n} of {total}',
  'gaps.showPages': 'Show the pages',
  'gaps.hidePages': 'Hide',
  'gaps.command': 'Run it yourself',
  'gaps.notActionable': 'Reported, not actionable here',
  'gaps.staleness': 'Read {when}',

  // The distinction the coverage matrix cannot draw, said once, where it
  // stops a reader concluding the app is under-reporting.
  'gaps.hollowDot':
    'An empty cell in the coverage matrix is only counted here when capture actually holds the file. A quarter the exchange has not published yet is not a gap.',

  'gaps.notParsed': '{n} not parsed',
  'gaps.notParsedTitle': '{missing} downloaded and never read, {stalled} stopped part-way. Separate from the pass rate, which is about the documents that were parsed.',
  'gaps.kind.never_ingested': 'Downloaded, never read',
  'gaps.kind.never_ingestedWhy':
    'Capture downloaded the PDF and the parser has no row for it. This is the gap the coverage matrix does not show.',
  'gaps.kind.stalled': 'Stopped part-way',
  'gaps.kind.stalledWhy':
    'Ingested, but never reached a terminal status. Each one resumes from its own rung of the ladder.',
  'gaps.kind.page_never_extracted': 'Pages never sent',
  'gaps.kind.page_never_extractedWhy':
    'The work plan asked for these pages and no answer was ever stored for them.',
  'gaps.kind.extraction_failed': 'Extraction failed',
  'gaps.kind.extraction_failedWhy':
    'The page was sent and could not be read. A page with no row at all is a different fact — it was never sent.',
  'gaps.kind.failing_check': 'Failing checks',
  'gaps.kind.failing_checkWhy':
    'Open review-queue entries. This is what the pass rate on the card is made of — not missing coverage.',
  'gaps.kind.image_missing': 'Page images missing',
  'gaps.kind.image_missingWhy':
    'No file on disk at the tier the plan assigned this page.',
  'gaps.kind.concept_unmapped': 'Facts with no concept',
  'gaps.kind.concept_unmappedWhy':
    'Stored with concept = NULL, so they cannot be compared across companies. Attaching them means re-normalizing.',
  'gaps.kind.no_ground_truth': 'Nothing to check against',
  'gaps.kind.no_ground_truthWhy':
    'No XBRL sibling was published for these filings, so no figure in them has been scored against the exchange’s own numbers.',

  // --- the corpus gaps screen ------------------------------------------
  'corpus.gaps.title': 'Gaps across the corpus',
  'corpus.gaps.subtitle': 'Pick the companies to fill. They run one at a time, in the background.',
  'corpus.gaps.company': 'Company',
  'corpus.gaps.parsed': 'Parsed',
  'corpus.gaps.neverRead': 'Never read',
  'corpus.gaps.stalled': 'Stalled',
  'corpus.gaps.incomplete': 'Incomplete',
  'corpus.gaps.review': 'Open checks',
  'corpus.gaps.planned': 'Planned tokens',
  'corpus.gaps.selectAll': 'All',
  'corpus.gaps.selectNone': 'None',
  'corpus.gaps.selectIncomplete': 'Only incomplete',
  'corpus.gaps.selected': '{n} selected',
  'corpus.gaps.nothing': 'Nothing selected',
  'corpus.gaps.runSelected': 'Fill {n} companies',
  'corpus.gaps.queueNote': 'One company at a time. A failure is recorded and the rest carry on.',
  'corpus.gaps.overCap': 'Over the configured cap of {cap} tokens — select fewer.',
  'corpus.gaps.overCapFull': 'This selection estimates {n} tokens, over the {cap} cap. Select fewer companies, or raise EXPLORER_ACTIONS_MAX_TOKENS in .env.',
  'corpus.gaps.capRoom': '{n} of {cap} tokens',
  'corpus.gaps.allDone': 'Every company is fully parsed.',
  'corpus.gaps.running': 'Already running',
  'batch.title': 'Before this batch runs',
  'batch.companies': 'Companies',
  'batch.plannedDocs': 'Documents with a plan',
  'batch.unplanned': 'Never probed',
  'batch.unplannedNote': 'These have no work plan, so their cost is unknown until phase 2 runs. At the corpus average that would add roughly {n} tokens — an extrapolation, not a measurement.',
  'batch.skipped': '{n} skipped: {why}',
  'batch.started': 'Queued {n} companies',

  // --- governance, remuneration, subsidiaries ---------------------------
  'gov.people': 'People',
  'gov.memberships': 'Seats',
  'gov.flagged': '{n} rows have no readable name',
  'gov.flagged_one': '{n} row has no readable name',
  'gov.flaggedWhy': 'A roster page numbers its rows, and where the model read the ordinal as the name the row lands here looking like a person called "3". They are kept rather than hidden — this is exactly the kind of misreading this app exists to surface.',
  'gov.body': 'Body',
  'gov.role': 'Role',
  'gov.classification': 'Classification',
  'gov.years': 'Years',
  'gov.name': 'Name',
  'gov.unnamed': 'no name read',
  'gov.body.board': 'Board',
  'gov.body.other': 'Other',
  'rem.name': 'Name',
  'rem.total': 'Total',
  'rem.asPrinted': 'As printed',
  'rem.year': 'Year',
  'rem.allYears': 'All years',
  'rem.typedNote': 'The total is the value phase 10 derived. The cells beneath it are what the page printed, under the heading the page used.',
  'sub.name': 'Name',
  'sub.capital': 'Capital',
  'sub.ownership': 'Ownership',
  'sub.activity': 'Main activity',
  'sub.operation': 'Operates in',
  'sub.incorporation': 'Incorporated in',
  'empty.governanceNow': 'No board or committee rows for this company yet. This data comes from board reports — fill the gaps to add them.',
  'empty.remunerationNow': 'No remuneration rows for this company yet. This data comes from board reports.',
  'empty.subsidiariesNow': 'No subsidiaries were extracted for this company yet.',

  // --- authentication ---------------------------------------------------
  'auth.signIn': 'Sign in',
  'auth.signOut': 'Sign out',
  'auth.username': 'Username',
  'auth.password': 'Password',
  'auth.displayName': 'Display name',
  'auth.displayNameHint': 'Optional — how your name appears in the app',
  'auth.submit': 'Sign in',
  'auth.signingIn': 'Signing in…',
  'auth.tagline': 'parsed filings, next to the paper they came from',
  'auth.setupTitle': 'Create the first account',
  'auth.setupWhy': 'No accounts exist yet. The first one is an administrator, and this form closes for good once it is created.',
  'auth.setupSubmit': 'Create administrator',
  'auth.registerTitle': 'Create an account',
  'auth.registerSubmit': 'Create account',
  'auth.registerClosed': 'Registration is closed. Ask an administrator for an account.',
  'auth.registerPending': 'Your account was created and is waiting for an administrator to approve it.',
  'auth.haveAccount': 'Sign in instead',
  'auth.needAccount': 'Create an account',
  'auth.inviteCode': 'Invite code',
  'auth.changeTitle': 'Set a new password',
  'auth.changeWhy': 'Your password was set by an administrator. Choose your own before continuing.',
  'auth.currentPassword': 'Current password',
  'auth.newPassword': 'New password',
  'auth.confirmPassword': 'Confirm new password',
  'auth.mismatch': 'The two passwords do not match',
  'auth.changeSubmit': 'Change password',
  'auth.changedSignIn': 'Password changed. Sign in with the new one.',
  'auth.minLength': 'At least {n} characters',
  'auth.signedInAs': 'Signed in as {name}',
  'auth.account': 'Account',

  // --- the admin panel --------------------------------------------------
  'admin.title': 'Users',
  'admin.subtitle': 'Who may sign in, and what they may do.',
  'admin.newUser': 'Add user',
  'admin.username': 'Username',
  'admin.name': 'Name',
  'admin.role': 'Role',
  'admin.role.admin': 'Administrator',
  'admin.role.viewer': 'Viewer',
  'admin.roleWhy': 'An administrator sees quality, gaps and runs, and can spend on extraction. A viewer sees the corpus.',
  'admin.status': 'Status',
  'admin.active': 'Active',
  'admin.disabled': 'Disabled',
  'admin.pending': 'Awaiting approval',
  'admin.created': 'Created',
  'admin.lastLogin': 'Last sign-in',
  'admin.never': 'never',
  'admin.sessions': 'Sessions',
  'admin.actions': 'Actions',
  'admin.enable': 'Enable',
  'admin.disable': 'Disable',
  'admin.approve': 'Approve',
  'admin.resetPassword': 'Reset password',
  'admin.signOutAll': 'End sessions',
  'admin.delete': 'Delete',
  'admin.deleteConfirm': 'Delete {name}? This cannot be undone.',
  'admin.you': 'you',
  'admin.registration': 'Registration',
  'admin.registration.closed': 'Closed — admins create accounts',
  'admin.registration.invite': 'Invite only',
  'admin.registration.request': 'Anyone may ask, admin approves',
  'admin.invites': 'Invites',
  'admin.newInvite': 'Create invite',
  'admin.inviteLink': 'Invite link',
  'admin.inviteExpires': 'Expires',
  'admin.inviteUsed': 'Used',
  'admin.revoke': 'Revoke',
  'admin.noInvites': 'No invites',
  'admin.tempPassword': 'Temporary password',
  'admin.mustChange': 'Require a password change at first sign-in',
  'admin.created1': 'Account created. Give them this password out of band — it is not shown again.',

  // --- jobs -----------------------------------------------------------
  'job.title': 'Runs',
  'job.running': 'Running',
  'job.queued': 'Queued',
  'job.done': 'Finished',
  'job.failed': 'Failed',
  'job.cancelled': 'Cancelled',
  'job.interrupted': 'Interrupted',
  'job.interruptedWhy': 'The server stopped while this was running. Resume picks up from the same phase.',
  'job.step': 'Step {n} of {total}',
  'job.stepNothingToDo': 'nothing to do',
  'job.cancel': 'Cancel',
  'job.resume': 'Resume',
  'job.showLog': 'Log',
  'job.hideLog': 'Hide log',
  'job.logEmpty': 'The phase has not written anything yet.',
  'job.none': 'No runs yet',
  'job.earlier': 'Earlier runs',
  'job.elapsed': '{d} elapsed',
  'job.remaining': '~{d} left',
  'job.reconnecting': 'Reconnecting…',
  'job.leaveSafe': 'This keeps running if you leave.',
  'job.disabled': 'Actions are disabled',
  'job.disabledWhy':
    'This build can read the corpus but not change it. Every gap below still names the command that would close it. Set EXPLORER_ACTIONS_ENABLED=true to enable them — read the security note in the plan first: this app has no login.',

  'job.outcome': 'What changed',
  'job.outcomeNone': 'Nothing changed.',
  'job.outcomeChecks': '{n} more checks pass',
  'job.outcomeChecksWorse': '{n} fewer checks pass',
  'job.outcomeReview': '{n} fewer open',
  'job.outcomeReviewWorse': '{n} more open',
  'job.outcomeFacts': '{n} facts',
  'job.outcomeConcepts': '{n} newly mapped to a concept',
  'job.outcomePages': '{n} pages read',

  // --- estimating before spending --------------------------------------
  'estimate.title': 'Before this runs',
  'estimate.documents': 'Documents',
  'estimate.pages': 'Pages',
  'estimate.tokens': 'Estimated input tokens',
  'estimate.cost': 'Estimated cost',
  'estimate.unknown': 'unknown until probed',
  'estimate.noCost': 'no price configured',
  'estimate.free': 'No model calls',
  'estimate.steps': 'Phases to run',
  'estimate.confirm': 'Run it',
  'estimate.cancel': 'Not now',
  'estimate.calibration': 'Measured against {docs} finished documents, est_tokens runs {factor}× actual. Budget about {budget}× the figure above.',
  'estimate.lowerBound': 'This is a lower bound',

  'action.run': 'Run',
  'action.reread': 'Re-read',
  'action.fillAll': 'Fill every gap',
} as const

export type Key = keyof typeof en

/**
 * The CLDR plural categories English does not have, and Arabic does.
 *
 * English needs two forms, so `_one` beside the base key says everything
 * there is to say. Arabic needs six: the dual is its own form, 3–10 takes a
 * broken plural, 11–99 takes a singular accusative, and 100 takes a singular
 * genitive -- which is why `43 مستنداً` and `4 مستندات` are both right and
 * neither is a spelling of the other.
 *
 * So a dictionary may carry more plural forms than this one does, and the
 * base key stays the `other` form. Required keys are still required: a
 * missing *translation* is a compile error, an unused plural category is
 * simply absent.
 */
type PluralSuffix = 'zero' | 'two' | 'few' | 'many'

export type Dict =
  & Record<Key, string>
  & Partial<Record<`${Key}_${PluralSuffix}`, string>>
